import { useEffect, useMemo, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { Bar } from "react-chartjs-2";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import Layout from "../../components/layoutConfig/layout";
import { BsFileEarmarkText, BsPeople, BsCalendar3 } from "react-icons/bs";
import LocalStorage from "../../helpers/localstorage";
import { setupAPIclient } from "../../services/setupAPIclient";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, ChartTooltip, Legend);

interface Doc {
  id: number;
  name: string | null;
  data_vencimento: string;
  management_uploaddoc_reminder: { reminder_days: number }[];
  management_user_uploaddoc_access: { management_user: { name: string } }[];
  management_document_access: {
    management_company?: { name_company: string };
    management_region?: { name: string };
    management_unit?: { name: string };
  }[];
}

function HomePage() {
  const [docs, setDocs] = useState<Doc[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const localStorage = new LocalStorage();
  const token = String(localStorage.getAuthToken());
  const api = setupAPIclient(token);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [resDocs, resUsers] = await Promise.all([
          api.post("/management/get/dashboard"),
          api.post("/management/get/users"),
        ]);
        setDocs(resDocs.data.docs || []);
        setUsers(resUsers.data?.success || []);
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const chartDataMap: Record<string, number> = {};
  docs.forEach((d) => {
    d.management_document_access.forEach((access) => {
      const key = [
        access.management_company?.name_company ?? "",
        access.management_region?.name ?? "",
        access.management_unit?.name ?? "",
      ]
        .filter(Boolean)
        .join(" / ");

      if (!key) return;
      chartDataMap[key] = (chartDataMap[key] || 0) + 1;
    });
  });

  const chartLabels = Object.keys(chartDataMap);
  const chartValues = Object.values(chartDataMap);

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        type: "bar" as const,
        label: "Contratos",
        data: chartValues,
        borderRadius: 10,
        backgroundColor: "rgba(93, 173, 226, 0.65)",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" as const },
      title: { display: true, text: "Performance por Empresa / Região / Unidade" },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          callback: (value: number | string) => {
            const label = chartLabels[Number(value)] || "";
            return label.length > 18 ? `${label.slice(0, 18)}...` : label;
          },
        },
      },
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
  };

  const totalContracts = docs.length;
  const totalUsers = users.length;
  const expiringContracts = docs.filter((d) => {
    const reminderDays = d.management_uploaddoc_reminder?.[0]?.reminder_days ?? 0;
    const diffDays = (new Date(d.data_vencimento).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24);
    return diffDays >= 0 && diffDays <= reminderDays;
  }).length;

  const eventsForSelectedDate = useMemo(() => {
    return docs.filter((d) => {
      const date = new Date(d.data_vencimento);
      return (
        date.getUTCFullYear() === selectedDate.getUTCFullYear() &&
        date.getUTCMonth() === selectedDate.getUTCMonth() &&
        date.getUTCDate() === selectedDate.getUTCDate()
      );
    });
  }, [docs, selectedDate]);

  return (
    <Layout>
      <div className="content mt-3">
        {loading ? (
          <p>Carregando...</p>
        ) : (
          <>
            <Row className="mb-3">
              <Col md={4} className="mb-3">
                <Card className="dashboard-grid-card p-3 h-100">
                  <div className="dashboard-stat">
                    <div className="dashboard-stat-icon"><BsFileEarmarkText size={18} /></div>
                    <div>
                      <div className="text-muted small">Contratos</div>
                      <div className="h4 mb-0">{totalContracts}</div>
                    </div>
                  </div>
                </Card>
              </Col>
              <Col md={4} className="mb-3">
                <Card className="dashboard-grid-card p-3 h-100">
                  <div className="dashboard-stat">
                    <div className="dashboard-stat-icon"><BsPeople size={18} /></div>
                    <div>
                      <div className="text-muted small">Signatários</div>
                      <div className="h4 mb-0">{totalUsers}</div>
                    </div>
                  </div>
                </Card>
              </Col>
              <Col md={4} className="mb-3">
                <Card className="dashboard-grid-card p-3 h-100">
                  <div className="dashboard-stat">
                    <div className="dashboard-stat-icon"><BsCalendar3 size={18} /></div>
                    <div>
                      <div className="text-muted small">Próximos a vencer</div>
                      <div className="h4 mb-0">{expiringContracts}</div>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col md={8} className="mb-3">
                <Card className="dashboard-grid-card p-3 h-100">
                  <div className="dashboard-section-title mb-2">Visão de Contratos</div>
                  <div style={{ minHeight: 360 }}>
                    <Bar data={chartData} options={chartOptions} />
                  </div>
                </Card>
              </Col>

              <Col md={4} className="mb-3">
                <Card className="dashboard-grid-card p-3 h-100 dashboard-mini-calendar">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="dashboard-section-title">Agenda rápida</div>
                    <Link to="/AgendaPage" className="btn btn-sm modern-primary-btn">Abrir agenda</Link>
                  </div>

                  <Calendar
                    locale="pt-BR"
                    onChange={(v) => setSelectedDate(v as Date)}
                    value={selectedDate}
                    formatDay={(_, date) => format(date, "dd", { locale: ptBR })}
                    tileContent={({ date }) => {
                      const hasEvent = docs.some((d) => {
                        const x = new Date(d.data_vencimento);
                        return (
                          x.getUTCFullYear() === date.getUTCFullYear() &&
                          x.getUTCMonth() === date.getUTCMonth() &&
                          x.getUTCDate() === date.getUTCDate()
                        );
                      });
                      return hasEvent ? <span style={{ color: "rgb(93, 173, 226)", fontSize: 11 }}>●</span> : null;
                    }}
                  />

                  <div className="mt-3">
                    <div className="small text-muted mb-2">
                      {format(selectedDate, "dd/MM/yyyy", { locale: ptBR })} - {eventsForSelectedDate.length} evento(s)
                    </div>
                    <div className="d-flex flex-column gap-2">
                      {eventsForSelectedDate.slice(0, 3).map((event) => (
                        <div className="agenda-event" key={event.id}>
                          <div className="fw-semibold">{event.name || "Sem nome"}</div>
                        </div>
                      ))}
                      {eventsForSelectedDate.length === 0 && <div className="agenda-event">Nenhum evento.</div>}
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </div>
    </Layout>
  );
}

export default HomePage;
